import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import type { FC } from "react"
import React, { useMemo } from "react"

import useSetNewPrimaryColumns from "./useSetNewPrimaryColumns"

import CircularLoadingIndicator from "@/components/common/LoadingIndicators/CircularLoadingIndicator"
import { useGetContacts } from "@/screens/Companies/hooks/useGetContacts"

interface PrimaryContactTableProps {
  companyId: string
  locationId: string
  currentContactId: string | null
  selectedPrimaryContactId: string | null
  setSelectedPrimaryContactId: (contactId: string | null) => void
  showCurrentContact: boolean
}
const PrimaryContactTable: FC<PrimaryContactTableProps> = ({
  companyId,
  locationId,
  currentContactId,
  selectedPrimaryContactId,
  setSelectedPrimaryContactId,
  showCurrentContact = true,
}) => {
  const { contactList, contactListFetching } = useGetContacts(companyId)

  const contactListToDisplay = useMemo(
    () =>
      contactList?.filter(
        (contact) =>
          contact.isActive &&
          (showCurrentContact || contact.contactId !== currentContactId) &&
          contact.contactLocations.find((l) => l.location.locationId === locationId)
      ) || [],
    [contactList, currentContactId, locationId, showCurrentContact]
  )

  const columns = useSetNewPrimaryColumns({
    selectedPrimaryContactId,
    setSelectedPrimaryContactId,
  })

  const table = useReactTable({
    data: contactListToDisplay,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (contactListFetching) {
    return <CircularLoadingIndicator />
  }

  return (
    <div className='flex size-full flex-col'>
      <div className='min-h-0 w-full flex-1 overflow-y-auto'>
        <TableContainer component={Paper} className='mt-2 shadow-none'>
          <Table>
            <TableHead className='h-10 border-b border-gray-500'>
              <TableRow>
                {table?.getHeaderGroups()?.map((headerGroup) =>
                  headerGroup.headers.map((header) => (
                    <TableCell key={header.id} className='h-[64px] text-white' data-testid={header.id} padding='none'>
                      <Typography variant='subtitle2' className='text-gray-900'>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </Typography>
                    </TableCell>
                  ))
                )}
              </TableRow>
            </TableHead>
            <TableBody data-testid='tableBody' className='table-row-group'>
              {table?.getRowModel().rows?.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      data-testid={`${row.id}-${cell.id}`}
                      padding='none'
                      className='h-[64px] text-gray-700'
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}

export default PrimaryContactTable
